import React, { useEffect, useState } from "react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Trash2, Mail, Phone, Building2, Check, Archive, Eye } from "lucide-react";

const STATUS_OPTIONS = [
  { v: "new", label: "New", color: "bg-[#DE9E36]" },
  { v: "read", label: "Read", color: "bg-blue-500" },
  { v: "replied", label: "Replied", color: "bg-green-600" },
  { v: "archived", label: "Archived", color: "bg-gray-500" },
];

const AdminQuotations = () => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contacts");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contacts/${id}`, { status });
      setItems((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success(`Status → ${status}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quotation permanently?")) return;
    try {
      await api.delete(`/admin/contacts/${id}`);
      setItems((list) => list.filter((x) => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = items.filter((it) => {
    if (filter && it.status !== filter) return false;
    return true;
  });

  const statusBadge = (st) => {
    const found = STATUS_OPTIONS.find((s) => s.v === st) || STATUS_OPTIONS[0];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.2em] uppercase rounded-full text-white ${found.color}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-white" />
        {found.label}
      </span>
    );
  };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="overline text-[#DE9E36]">Inbox</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-[#1A201C] tracking-tight">
            Quotations
          </h1>
          <p className="mt-2 text-sm text-[#4E5750]">
            {items.length} total · {items.filter((x) => x.status === "new").length} new
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setFilter("")}
            className={`px-3 py-1.5 text-xs tracking-[0.2em] uppercase border ${
              !filter ? "bg-[#1A201C] text-white border-[#1A201C]" : "border-[#E5E0D8] text-[#4E5750] hover:bg-white"
            }`}
          >
            All
          </button>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.v}
              onClick={() => setFilter(s.v)}
              data-testid={`filter-${s.v}`}
              className={`px-3 py-1.5 text-xs tracking-[0.2em] uppercase border ${
                filter === s.v
                  ? "bg-[#1A201C] text-white border-[#1A201C]"
                  : "border-[#E5E0D8] text-[#4E5750] hover:bg-white"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* List */}
        <div className="lg:col-span-7 border border-[#E5E0D8] bg-white overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-sm text-[#4E5750]">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#4E5750]">No quotations.</div>
          ) : (
            <ul className="divide-y divide-[#E5E0D8]">
              {filtered.map((q) => (
                <li
                  key={q.id}
                  onClick={() => {
                    setSelected(q);
                    if (q.status === "new") setStatus(q.id, "read");
                  }}
                  data-testid={`quote-row-${q.id}`}
                  className={`p-5 cursor-pointer hover:bg-[#F3EFE7] transition-colors ${
                    selected?.id === q.id ? "bg-[#F3EFE7]" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="text-sm font-semibold text-[#1A201C]">{q.name}</div>
                      <div className="text-xs text-[#4E5750]">{q.email}{q.company ? ` · ${q.company}` : ""}</div>
                    </div>
                    {statusBadge(q.status)}
                  </div>
                  <div className="text-sm text-[#4E5750] line-clamp-2">{q.message}</div>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-[#8A8D87]">
                    <span>{new Date(q.created_at).toLocaleString()}</span>
                    <span className="font-mono uppercase">{q.language}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-5">
          {selected ? (
            <div className="sticky top-6 border border-[#E5E0D8] bg-white p-7">
              <div className="flex items-start justify-between gap-3 mb-6">
                <div>
                  <h2 className="text-xl font-bold text-[#1A201C] tracking-tight">
                    {selected.name}
                  </h2>
                  <div className="text-xs text-[#4E5750] mt-1">
                    {new Date(selected.created_at).toLocaleString()}
                  </div>
                </div>
                {statusBadge(selected.status)}
              </div>

              <div className="space-y-3 text-sm">
                <Info icon={Mail} value={<a href={`mailto:${selected.email}`} className="hover:text-[#DE9E36]">{selected.email}</a>} />
                {selected.phone && <Info icon={Phone} value={<a href={`tel:${selected.phone}`} className="hover:text-[#DE9E36]">{selected.phone}</a>} />}
                {selected.company && <Info icon={Building2} value={selected.company} />}
              </div>

              {selected.product_interest && (
                <div className="mt-5 p-3 bg-[#F3EFE7] text-sm">
                  <div className="overline text-[#DE9E36] mb-1">Product of interest</div>
                  {selected.product_interest}
                </div>
              )}

              <div className="mt-6">
                <div className="overline text-[#4E5750] mb-2">Message</div>
                <p className="text-sm leading-relaxed whitespace-pre-wrap text-[#1A201C]">
                  {selected.message}
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                <button
                  onClick={() => setStatus(selected.id, "read")}
                  data-testid="action-read"
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-[0.2em] uppercase border border-[#E5E0D8] hover:bg-[#F3EFE7]"
                >
                  <Eye size={14} /> Mark read
                </button>
                <button
                  onClick={() => setStatus(selected.id, "replied")}
                  data-testid="action-replied"
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-[0.2em] uppercase bg-green-600 text-white hover:bg-green-700"
                >
                  <Check size={14} /> Replied
                </button>
                <button
                  onClick={() => setStatus(selected.id, "archived")}
                  data-testid="action-archive"
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-[0.2em] uppercase border border-[#E5E0D8] hover:bg-[#F3EFE7]"
                >
                  <Archive size={14} /> Archive
                </button>
                <button
                  onClick={() => remove(selected.id)}
                  data-testid="action-delete"
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-[0.2em] uppercase text-red-700 border border-red-200 hover:bg-red-50"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-[#E5E0D8] p-10 text-center text-sm text-[#4E5750]">
              Select a quotation to view details.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Info = ({ icon: Icon, value }) => (
  <div className="flex items-center gap-3 text-[#1A201C]">
    <Icon size={15} strokeWidth={1.5} className="text-[#DE9E36]" />
    <span>{value}</span>
  </div>
);

export default AdminQuotations;
